/**
 * canvas-engine/presets/presetData/retailPresets.js
 *
 * TRANSCRIBED Retail (storefront) default look-and-feel VALUES.
 *
 * ZERO-REUSE CONSTRAINT (Task 3.1): imports NOTHING. Values transcribed from
 * the Default_Source (`retailTemplateDefaults.js` + `ConsoleRetailPreview`)
 * as plain DATA. No React, no rendering, no side effects.
 *
 * See educationPresets.js for the shared descriptor shape. Consumed by the own
 * decomposition adapters (Task 3.2/3.3).
 *
 * Requirements: 3.6, 3.7, 5.1, 5.5, 5.6, 5.7
 */

/** Default imagery (transcribed from `retailImages`). */
export const retailImages = {
  BANNER_1: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1920&q=85&fit=crop&auto=format',
  BANNER_2: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=1920&q=85&fit=crop&auto=format',
  CATEGORY_FASHION: 'https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=600&q=75&fit=crop&auto=format',
  CATEGORY_HOME: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600&q=75&fit=crop&auto=format',
  CATEGORY_ELECTRONICS: 'https://images.unsplash.com/photo-1498049794561-7780e7231661?w=600&q=75&fit=crop&auto=format',
  CATEGORY_BEAUTY: 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=600&q=75&fit=crop&auto=format',
  PRODUCT_ONE: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=700&q=80&fit=crop&auto=format',
  PRODUCT_TWO: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=700&q=80&fit=crop&auto=format',
  PRODUCT_THREE: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=700&q=80&fit=crop&auto=format',
  PRODUCT_FOUR: 'https://images.unsplash.com/photo-1543163521-1bf539c55dd2?w=700&q=80&fit=crop&auto=format',
  ABOUT: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200&q=85&fit=crop&auto=format',
  CONTACT: 'https://images.unsplash.com/photo-1556745757-8d76bdb6984b?w=1200&q=85&fit=crop&auto=format',
};

/** Theme-level style values transcribed from Retail `themeDefaults`. */
export const retailTheme = {
  primary: '#0891b2',
  secondary: '#232f3e',
  accent: '#B88E2F',
  background: '#f8fafc',
  surface: '#ffffff',
  color: '#111827',
  mutedColor: '#6b7280',
  borderColor: '#e5e7eb',
  baseFontSize: '16px',
  headingWeight: '800',
  lineHeight: '1.6',
  pageWidth: '1280px',
  sectionSpacing: '72px 24px',
  gridGap: '24px',
  buttonRadius: '999px',
  cardRadius: '14px',
  cardShadow: '0 12px 28px rgba(15, 23, 42, 0.12)',
  imageRadius: '12px',
  headerHeight: '80px',
  footerBackground: '#232f3e',
};

// ── Local pure data helpers ──
const img = (url = '') => ({ storedLink: '', displayLink: url });
const action = (label, url, variant = 'primary') => ({ label, url, variant, visible: true });
const block = (title, description = '', extra = {}) => ({ title, description, visible: true, ...extra });
const style = (overrides = {}) => ({
  background: '#ffffff',
  textColor: '#111827',
  accentColor: '#0891b2',
  secondaryColor: '#232f3e',
  padding: '72px 24px',
  ...overrides,
});

const categoryCards = () => [
  block('Fashion', 'Fresh apparel, footwear, and accessories.', { imageContent: img(retailImages.CATEGORY_FASHION), url: '/category/Fashion' }),
  block('Home Decor', 'Curated essentials for beautiful living.', { imageContent: img(retailImages.CATEGORY_HOME), url: '/category/Home Decor' }),
  block('Electronics', 'Useful devices and everyday tech.', { imageContent: img(retailImages.CATEGORY_ELECTRONICS), url: '/category/Electronics' }),
  block('Beauty', 'Self-care picks and personal favorites.', { imageContent: img(retailImages.CATEGORY_BEAUTY), url: '/category/Beauty' }),
];

const productCards = () => [
  block('Urban Runner Sneakers', 'Lightweight comfort for daily movement.', { imageContent: img(retailImages.PRODUCT_ONE), price: '$84.00', compareAt: '$110.00', badge: 'Best Seller', rating: '4.8', buttonLabel: 'Add to Cart' }),
  block('Minimal Smart Watch', 'Track time, wellness, and notifications.', { imageContent: img(retailImages.PRODUCT_TWO), price: '$129.00', compareAt: '$159.00', badge: 'New', rating: '4.7', buttonLabel: 'Add to Cart' }),
  block('Studio Headphones', 'Rich sound with an everyday fit.', { imageContent: img(retailImages.PRODUCT_THREE), price: '$96.00', compareAt: '$124.00', badge: 'Deal', rating: '4.9', buttonLabel: 'Add to Cart' }),
  block('Classic Leather Heels', 'Polished style for work and evenings.', { imageContent: img(retailImages.PRODUCT_FOUR), price: '$76.00', compareAt: '$98.00', badge: 'Limited', rating: '4.6', buttonLabel: 'Add to Cart' }),
];

/**
 * RETAIL_PRESETS — per-section-type default descriptors.
 * Covers heroCarousel, categoryGrid, featuredProducts, productGrid,
 * productDetail, cart, orderSummary, about, contact, promoBanner.
 */
export const RETAIL_PRESETS = {
  retailHeroCarousel: {
    sectionType: 'retailHeroCarousel',
    label: 'Retail hero carousel',
    domain: 'retail',
    style: style({ padding: '0', minHeight: '460px' }),
    layout: { align: 'center', width: 'full' },
    content: {
      eyebrow: 'Seasonal Collection',
      heading: 'Shop fresh arrivals, daily essentials, and curated deals.',
      subheading:
        'A polished retail storefront with categories, product rails, cart, checkout, and editable merchandising sections.',
      images: [img(retailImages.BANNER_1), img(retailImages.BANNER_2)],
      actions: [
        action('Shop now', '/product', 'primary'),
        action('View categories', '/category/Fashion', 'outline'),
      ],
    },
  },

  retailCategoryGrid: {
    sectionType: 'retailCategoryGrid',
    label: 'Shop categories',
    domain: 'retail',
    style: style({ background: '#ffffff', padding: '56px 24px' }),
    layout: { align: 'center', width: 'contained', columns: 4 },
    content: {
      heading: 'Shop By Category',
      subheading: 'Explore curated collections by category.',
      tileShape: 'circle',
      cards: categoryCards(),
    },
  },

  retailFeaturedProducts: {
    sectionType: 'retailFeaturedProducts',
    label: 'Featured products rail',
    domain: 'retail',
    style: style({ background: '#f8fafc', padding: '64px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 4 },
    content: {
      heading: 'Featured Products',
      subheading: 'Best-selling products selected for today.',
      buttonLabel: 'Add to Cart',
      cards: productCards(),
      // Live featured products may render in place of these defaults.
      binding: { source: 'products' },
    },
  },

  retailProductGrid: {
    sectionType: 'retailProductGrid',
    label: 'Product listing',
    domain: 'retail',
    style: style({ background: '#f8fafc', padding: '72px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 4 },
    content: {
      heading: 'All Products',
      subheading: 'Browse the full catalog with filters and sorting.',
      filterTitle: 'Filters',
      searchPlaceholder: 'Search products...',
      sortLabel: 'Sort by',
      buttonLabel: 'Add to Cart',
      categories: categoryCards(),
      cards: productCards(),
      binding: { source: 'products' },
    },
  },

  retailProductDetail: {
    sectionType: 'retailProductDetail',
    label: 'Product detail',
    domain: 'retail',
    style: style({ background: '#ffffff', padding: '72px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      badge: 'Best Seller',
      heading: 'Urban Runner Sneakers',
      subheading:
        'Comfortable everyday sneakers with a breathable upper, responsive sole, and clean modern profile.',
      images: [img(retailImages.PRODUCT_ONE)],
      priceLabel: '$84.00',
      compareAtLabel: '$110.00',
      ratingLabel: '4.8 rating from 240 reviews',
      quantityLabel: 'Quantity',
      actions: [
        action('Add to Cart', '#', 'primary'),
        action('Add to Wishlist', '#', 'outline'),
      ],
      blocks: [
        block('Free shipping', 'On orders over $75.'),
        block('Easy returns', '30-day return policy.'),
        block('Secure checkout', 'Protected payment experience.'),
      ],
      binding: { source: 'productDetail' },
    },
  },

  retailCart: {
    sectionType: 'retailCart',
    label: 'Cart',
    domain: 'retail',
    style: style({ background: '#f8fafc', padding: '72px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      heading: 'Shopping Cart',
      subheading: 'Review your items before checkout.',
      summaryTitle: 'Order Summary',
      subtotalLabel: 'Subtotal',
      taxLabel: 'Estimated tax',
      totalLabel: 'Total',
      checkoutLabel: 'Proceed to Checkout',
      cards: productCards().slice(0, 3),
      binding: { source: 'cart' },
    },
  },

  retailOrderSummary: {
    sectionType: 'retailOrderSummary',
    label: 'Order summary',
    domain: 'retail',
    style: style({ background: '#ffffff', padding: '72px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      heading: 'Order Summary',
      subheading: 'Track your latest retail orders and payment status.',
      statusLabel: 'Processing',
      totalLabel: '$309.00',
      buttonLabel: 'View details',
      cards: [
        block('Order #RT-1042', 'Placed today. Delivery estimate: 3-5 business days.', { price: '$309.00', badge: 'Processing' }),
        block('Payment', 'Visa ending in 4242.', { price: 'Paid', badge: 'Complete' }),
        block('Shipping', 'Standard shipping to saved address.', { price: 'Included', badge: 'In transit' }),
      ],
      binding: { source: 'orderHistory' },
    },
  },

  retailAbout: {
    sectionType: 'retailAbout',
    label: 'About retail store',
    domain: 'retail',
    style: style({ background: '#ffffff', padding: '88px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      eyebrow: 'About Our Store',
      heading: 'Curated products, clear value, and a smoother way to shop.',
      subheading:
        'We bring everyday essentials and premium finds into one simple retail experience, built for quick discovery and confident checkout.',
      images: [img(retailImages.ABOUT)],
      actions: [action('Start shopping', '/product', 'primary')],
      blocks: [
        block('Fast fulfillment', 'Reliable delivery and easy order tracking.', { value: '24h' }),
        block('Curated catalog', 'Products selected for quality and value.', { value: '500+' }),
        block('Customer support', 'Friendly help for shopping and returns.', { value: '7d' }),
      ],
    },
  },

  retailContact: {
    sectionType: 'retailContact',
    label: 'Contact retail store',
    domain: 'retail',
    style: style({ background: '#f8fafc', padding: '88px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      eyebrow: 'Contact Us',
      heading: 'Need help with an order?',
      subheading:
        'Reach our retail support team for product questions, delivery, returns, and checkout help.',
      phoneTitle: 'Phone',
      phoneValue: '+1 (678) 855-6990',
      emailTitle: 'Email',
      emailValue: 'system@avpstech.com',
      addressTitle: 'Store Office',
      addressValue: '123 Retail Avenue\nAtlanta, GA 30301',
      formTitle: 'Send a Message',
      submitLabel: 'Submit Message',
      fields: [
        { label: 'First Name', type: 'text', visible: true },
        { label: 'Last Name', type: 'text', visible: true },
        { label: 'Email *', type: 'email', visible: true },
        { label: 'Message *', type: 'textarea', visible: true },
      ],
    },
  },

  retailPromoBanner: {
    sectionType: 'retailPromoBanner',
    label: 'Promo banner',
    domain: 'retail',
    style: style({ background: '#232f3e', textColor: '#ffffff', padding: '56px 24px' }),
    layout: { align: 'center', width: 'contained' },
    content: {
      eyebrow: 'Limited Time',
      heading: 'Get seasonal deals before they are gone.',
      subheading:
        'Promote sales, newsletters, coupons, or special collections from one editable banner.',
      actions: [action('Shop the sale', '/product', 'primary')],
    },
  },

  // Category Products page (Default_Source: createRetailDefaultPages
  // `category` → legacyPageSection 'CategoryProducts'; ConsoleRetailPreview
  // renders RetailCategoryGrid + RetailProductGrid for the 'CategoryProducts'
  // key). Transcribed as a single category product-listing shell whose live
  // catalog region is sourced through a `products` binding.
  retailCategory: {
    sectionType: 'retailCategory',
    label: 'Category products',
    domain: 'retail',
    style: style({ background: '#f8fafc', padding: '72px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 4 },
    content: {
      eyebrow: 'Shop By Category',
      heading: 'Category Products',
      subheading: 'Browse products by category with filters and sorting.',
      filterTitle: 'Filters',
      searchPlaceholder: 'Search products...',
      sortLabel: 'Sort by',
      buttonLabel: 'Add to Cart',
      tileShape: 'circle',
      categories: categoryCards(),
      cards: productCards(),
      // Live category catalog renders in place of these default cards.
      binding: { source: 'products' },
    },
  },

  // Sign in / Sign up page (Default_Source: createRetailDefaultPages `signIn`
  // → 'Login', `signUp` → 'Signup'; ConsoleRetailPreview auth strings
  // 'Sign in to your shopping account' / 'Create your customer account').
  // Mirrors the waasAuth shape: a `signIn` default plus a
  // `content.variants.signUp` block preserved losslessly for the adapter to
  // swap when authVariant === 'signUp'.
  retailAuth: {
    sectionType: 'retailAuth',
    label: 'Auth page',
    domain: 'retail',
    style: style({ background: '#f8fafc', textColor: '#111827', padding: '92px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      // Default reflects the sign-in mode. Sign-up copy carried under
      // variants.signUp for the adapter to swap when mode === 'signUp'.
      mode: 'signIn',
      eyebrow: 'Account Access',
      heading: 'Sign in to your shopping account',
      subheading:
        'Sign in to track orders, manage your cart, view order history, and check out faster.',
      formTitle: 'Sign in',
      submitLabel: 'Sign in',
      alternateLabel: 'Need an account? Sign up',
      alternateUrl: '/sign-up',
      images: [img(retailImages.BANNER_1)],
      imageAlt: 'Retail storefront account access',
      fields: [
        { label: 'Email', type: 'email', required: true, visible: true },
        { label: 'Password', type: 'password', required: true, visible: true },
      ],
      blocks: [
        block('Faster checkout', 'Save addresses and payment details for one-tap ordering.', { step: '01' }),
        block('Order tracking', 'Follow every order from confirmation to delivery in one place.', { step: '02' }),
      ],
      // Sign-up variant (mode === 'signUp'). Preserved losslessly under
      // canvas.source.preserved.content and ignored by the default stack
      // layout until the adapter selects it for the sign-up page.
      variants: {
        signUp: {
          mode: 'signUp',
          eyebrow: 'Create Your Account',
          heading: 'Create your customer account',
          subheading:
            'Create an account to save your cart, track orders, manage returns, and check out faster next time.',
          formTitle: 'Create account',
          submitLabel: 'Create account',
          alternateLabel: 'Already have an account? Sign in',
          alternateUrl: '/sign-in',
          images: [img(retailImages.PRODUCT_ONE)],
          imageAlt: 'Retail storefront new account',
          fields: [
            { label: 'First Name', type: 'text', required: true, visible: true },
            { label: 'Last Name', type: 'text', required: true, visible: true },
            { label: 'Email', type: 'email', required: true, visible: true },
            { label: 'Phone Number', type: 'tel', required: false, visible: true },
            { label: 'Password', type: 'password', required: true, visible: true },
          ],
        },
      },
    },
  },
};

/** Section presets list (transcribed from `retailSectionPresets`). */
export const retailSectionPresets = [
  { type: 'customHtml', title: 'Custom section', description: 'Start blank with sanitized HTML and scoped CSS.', highlight: true },
  { type: 'retailHeroCarousel', title: 'Hero carousel', description: 'Large rotating sale banners.' },
  { type: 'retailCategoryGrid', title: 'Categories', description: 'Circular shop-by-category tiles.' },
  { type: 'retailFeaturedProducts', title: 'Featured rail', description: 'Horizontal product cards.' },
  { type: 'retailProductGrid', title: 'Products', description: 'Catalog and filter shell.' },
  { type: 'retailProductDetail', title: 'Product detail', description: 'Product detail shell.' },
  { type: 'retailCart', title: 'Cart', description: 'Cart and totals shell.' },
  { type: 'retailOrderSummary', title: 'Summary', description: 'Order summary shell.' },
  { type: 'retailCategory', title: 'Category', description: 'Category product listing shell.' },
  { type: 'retailAbout', title: 'About', description: 'Store story section.' },
  { type: 'retailContact', title: 'Contact', description: 'Store contact form.' },
  { type: 'retailPromoBanner', title: 'Promo', description: 'Sale or newsletter CTA.' },
  { type: 'retailAuth', title: 'Sign in / Sign up', description: 'Account access form section.' },
];

export default RETAIL_PRESETS;
